import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

function App() {
  const [currentPrice, setCurrentPrice] = useState(0.85);
  const [priceChange, setPriceChange] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState({
    days: 1000,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const canvasRef = useRef(null);

  // Calculate time until 1AB = 1USD
  useEffect(() => {
    // Target date: April 1, 2025, 00:01 UTC
    const targetDate = new Date('2025-04-01T00:01:00Z');
    
    // Calculate 1000 days after the target date
    const endDate = new Date(targetDate);
    endDate.setDate(endDate.getDate() + 1000);
    
    const timer = setInterval(() => {
      // Get current date in UTC
      const now = new Date();
      
      // Time remaining until the start date (April 1, 2025)
      const timeToStart = targetDate.getTime() - now.getTime();
      
      // Time remaining from start date until 1000 days later
      const totalTimeForProcess = endDate.getTime() - targetDate.getTime();
      
      // Total time remaining (time to start + 1000 days)
      const totalTimeRemaining = endDate.getTime() - now.getTime();
      
      if (totalTimeRemaining <= 0) {
        // If past the end date, show 0 for all values
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      // If we're before the start date, show more than 1000 days
      const days = Math.floor(totalTimeRemaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((totalTimeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((totalTimeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((totalTimeRemaining % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch AB price from API
  useEffect(() => {
    const fetchABPrice = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 通过本地代理服务器请求CoinMarketCap API
        // 使用相对URL，这样会自动使用当前网站的域名或IP
        const response = await fetch('/api/price');
        
        if (!response.ok) {
          throw new Error('Failed to fetch price data');
        }
        
        const data = await response.json();
        
        // 解析CoinMarketCap API的响应
        if (data.status.error_code !== 0) {
          throw new Error(data.status.error_message || 'Failed to fetch price data');
        }
        
        // 检查AB币的数据是否存在
        if (!data.data.AB) {
          throw new Error('AB token data not found');
        }
        
        const abData = data.data.AB;
        const price = abData.quote.USD.price;
        const percentChange24h = abData.quote.USD.percent_change_24h || 0;
        
        setCurrentPrice(price);
        setPriceChange(percentChange24h);
      } catch (err) {
        console.error('Error fetching price:', err);
        setError('Unable to fetch latest price data');
        
        // 如果API获取失败，保持使用模拟数据作为备用
        const fluctuation = (Math.random() - 0.5) * 0.01;
        setCurrentPrice(prevPrice => {
          const newPrice = prevPrice + fluctuation;
          return Number.parseFloat(newPrice.toFixed(4));
        });
      } finally {
        setLoading(false);
      }
    };
    
    // 页面加载时立即获取一次价格
    fetchABPrice();
    
    // 设置更新间隔，前端每5分钟请求一次服务器
    // 与服务器端缓存刷新时间保持一致
    const priceInterval = setInterval(fetchABPrice, 5 * 60 * 1000);
    
    return () => clearInterval(priceInterval);
  }, []);

  // Canvas animation for space and blockchain particles
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Particles array
    const particlesArray = [];
    const numberOfParticles = 100;

    // Resize event
    window.addEventListener("resize", () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    });

    // Particle class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * 1.5;
        this.speedY = (Math.random() - 0.5) * 1.5;
        this.color = `rgba(${Math.floor(Math.random() * 100 + 155)}, ${Math.floor(Math.random() * 100 + 155)}, 255, ${Math.random() * 0.8 + 0.2})`;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        if (!ctx) return;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Create particles
    for (let i = 0; i < numberOfParticles; i++) {
      particlesArray.push(new Particle());
    }

    // Animation function
    function animate() {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw stars
      for (let i = 0; i < 200; i++) {
        if (Math.random() < 0.01) {
          ctx.fillStyle = "white";
          ctx.beginPath();
          const x = Math.random() * canvas.width;
          const y = Math.random() * canvas.height;
          const size = Math.random() * 2;
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Draw and update particles
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();

        // Connect particles with lines (blockchain effect)
        for (let j = i; j < particlesArray.length; j++) {
          const dx = particlesArray[i].x - particlesArray[j].x;
          const dy = particlesArray[i].y - particlesArray[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.2 - distance / 500})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particlesArray[i].x, particlesArray[i].y);
            ctx.lineTo(particlesArray[j].x, particlesArray[j].y);
            ctx.stroke();
          }
        }
      }

      // Occasional lightning effect
      if (Math.random() < 0.01) {
        const startX = Math.random() * canvas.width;
        const startY = 0;
        let x = startX;
        let y = startY;

        ctx.beginPath();
        ctx.strokeStyle = "rgba(255, 255, 255, 0.8)";
        ctx.lineWidth = Math.random() * 3 + 1;
        ctx.moveTo(x, y);

        while (y < canvas.height) {
          x += (Math.random() - 0.5) * 80;
          y += Math.random() * 20 + 10;
          ctx.lineTo(x, y);
        }

        ctx.stroke();
      }

      requestAnimationFrame(animate);
    }

    animate();

    return () => {
      // Cleanup
      window.removeEventListener("resize", () => {});
    };
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden text-white">
      {/* Background Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-black via-blue-950 to-black"
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-4 text-center">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:gap-3 justify-center">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600 tracking-wider">
              Countdown to
            </h1>
            <h1 className="font-heading text-5xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-600 tracking-wider">
              1AB = 1USD
            </h1>
          </div>
        </motion.div>

        {/* Countdown */}
        <motion.div
          className="grid grid-cols-4 gap-6 md:gap-10 lg:gap-16 text-center mb-12"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <div className="flex flex-col">
            <span className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-blue-400">{timeLeft.days}</span>
            <span className="text-sm md:text-base text-blue-200 uppercase tracking-widest">Days</span>
          </div>
          <div className="flex flex-col">
            <span className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-blue-400">{timeLeft.hours}</span>
            <span className="text-sm md:text-base text-blue-200 uppercase tracking-widest">Hours</span>
          </div>
          <div className="flex flex-col">
            <span className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-blue-400">{timeLeft.minutes}</span>
            <span className="text-sm md:text-base text-blue-200 uppercase tracking-widest">Minutes</span>
          </div>
          <div className="flex flex-col">
            <span className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-blue-400">{timeLeft.seconds}</span>
            <span className="text-sm md:text-base text-blue-200 uppercase tracking-widest">Seconds</span>
          </div>
        </motion.div>

        {/* Current Price */}
        <motion.div
          className="bg-black/30 backdrop-blur-md p-6 rounded-xl border border-blue-500/30"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <div className="font-heading text-xl md:text-2xl mb-2 text-blue-200 tracking-wide">Current AB Price</div>
          {loading && <div className="text-blue-300 text-sm mb-2">Loading...</div>}
          {error && <div className="text-red-400 text-sm mb-2">{error}</div>}
          <div className="flex items-center justify-center gap-2">
            <span className="font-heading text-4xl md:text-5xl font-bold">${currentPrice.toFixed(4)}</span>
            <span
              className={`text-sm px-2 py-1 rounded ${
                priceChange > 0 
                  ? "bg-green-500/20 text-green-400" 
                  : priceChange < 0 
                    ? "bg-red-500/20 text-red-400" 
                    : "bg-blue-500/20 text-blue-300"
              }`}
            >
              {priceChange > 0 ? "+" : ""}
              {priceChange.toFixed(2)}%
            </span>
          </div>
          <div className="mt-4 flex justify-center">
            <a 
              href="https://coinmarketcap.com/currencies/newton/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-1 text-sm text-blue-300 hover:text-blue-100 transition-colors tracking-wider uppercase font-semibold"
            >
              View Trading Details <ArrowUpRight size={14} />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default App;
